// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import { BackButton } from '@advisor-app/components';
import {
  ButtonExecuteTrades,
  type ButtonExecuteTradesProps,
} from '@advisor-app/features/brokers';
import { FormAddTickerBuy } from '@advisor-app/features/operations/components/form-add-ticker-buy';
import { AddOperationsIconButton } from '@advisor-app/features/operations/components/add-operations-icon-button';
import { IconButton } from '@shared/ui-kit/components/icon-button';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { useMemo, type FC, type MouseEventHandler } from 'react';
import { ProposalModal } from '@shared/advisory/proposals';
import { type InternalProposalOperation } from '@shared/data-access/interfaces/api/brokerage/proposals-types';
import { useTranslation } from 'react-i18next';
import { useAdvisorRole } from '@advisor-app/hooks/use-advisor-role';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';

export type TradesActionButtons =
  | 'addTicker'
  | 'executeTrades'
  | 'downloadReport'
  | 'trader'
  | 'taxHarvesting'
  | 'rebalance'
  | 'savePortfolio'
  | 'downloadTrades'
  | 'deleteAllTrades'
  | 'sendProposals';

export type TradesEventMap = Record<
  `on${Capitalize<
    Exclude<TradesActionButtons, 'addTicker' | 'executeTrades' | 'sendProposals'>
  >}`,
  MouseEventHandler<HTMLButtonElement>
>;

export type TradesAttributesMap = Record<
  `disable${Capitalize<
    Exclude<TradesActionButtons, 'addTicker' | 'executeTrades' | 'sendProposals'>
  >}`,
  boolean
>;

export interface TradesActionToolbar
  extends Partial<TradesEventMap & TradesAttributesMap> {
  readonly buttons: Partial<Record<TradesActionButtons, boolean>>;
  readonly backTo?: Paths;
  readonly executeTrades?: ButtonExecuteTradesProps;
  readonly proposalOperations?: InternalProposalOperation[];
  readonly validProposal?: boolean;
}

export const TradesActionToolbar: FC<TradesActionToolbar> = ({
  buttons,
  backTo,
  onDownloadReport,
  onTrader,
  onDownloadTrades,
  onRebalance,
  onSavePortfolio,
  onTaxHarvesting,
  executeTrades,
  onDeleteAllTrades,
  disableTrader,
  proposalOperations,
  validProposal = true,
}) => {
  const { t } = useTranslation();
  const { canUseTrader, canSendProposals } = useAdvisorRole();

  const { executeTradesProps, hasTopRow, hasBottomRow, hasButtons } =
    useMemo(() => {
      const currentExecuteTradesProps =
        buttons.executeTrades && executeTrades?.data ? executeTrades : null;

      const currentHasTopRow = Boolean(
        backTo ||
          (buttons.sendProposals && canSendProposals) ||
          currentExecuteTradesProps,
      );

      const currentHasBottomRow = Boolean(
        (buttons.addTicker && canUseTrader) ||
          buttons.downloadReport ||
          (buttons.trader && canUseTrader) ||
          buttons.taxHarvesting ||
          buttons.rebalance ||
          buttons.savePortfolio ||
          buttons.downloadTrades ||
          buttons.deleteAllTrades,
      );

      return {
        executeTradesProps: currentExecuteTradesProps,
        hasTopRow: currentHasTopRow,
        hasBottomRow: currentHasBottomRow,
        hasButtons: currentHasTopRow || currentHasBottomRow,
      };
    }, [backTo, buttons, canSendProposals, canUseTrader, executeTrades]);

  return hasButtons ? (
    <div
      className={classNames('!mb-5 mt-[10px] flex flex-col items-end justify-end', {
        'gap-y-4': hasTopRow && hasBottomRow,
      })}
    >
      {hasTopRow && (
        <div className="flex flex-row items-center justify-center gap-x-5">
          {backTo && <BackButton to={backTo} />}
          {buttons.sendProposals && canSendProposals && (
            <ProposalModal
              operations={proposalOperations}
              validProposal={validProposal}
            />
          )}
          {executeTradesProps && <ButtonExecuteTrades {...executeTradesProps} />}
        </div>
      )}

      {hasBottomRow && (
        <div className="flex items-center space-x-4">
          {buttons.addTicker && <FormAddTickerBuy />}
          {buttons.downloadReport && (
            <Tooltip title={t('common.analysis-report', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Reporting"
                  onClick={onDownloadReport}
                  data-testid="test-reports"
                />
              </span>
            </Tooltip>
          )}
          {buttons.trader && onTrader && (
            <AddOperationsIconButton onClick={onTrader} disabled={disableTrader} />
          )}

          {buttons.taxHarvesting && (
            <Tooltip title={t('common.tax-harvesting', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="TaxManager"
                  onClick={onTaxHarvesting}
                  data-testid="tax-harvesting-test-button"
                />
              </span>
            </Tooltip>
          )}
          {buttons.rebalance && (
            <Tooltip title={t('common.rebalance', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Rebalance"
                  onClick={onRebalance}
                  data-testid="rebalance-test-button"
                />
              </span>
            </Tooltip>
          )}
          {buttons.savePortfolio && (
            <Tooltip title={t('common.save-portfolio', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Save"
                  onClick={onSavePortfolio}
                  data-testid="save"
                />
              </span>
            </Tooltip>
          )}
          {buttons.downloadTrades && (
            <Tooltip title={t('common.download-trades', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Download"
                  onClick={onDownloadTrades}
                  data-testid="trades-download-button"
                />
              </span>
            </Tooltip>
          )}
          {buttons.deleteAllTrades && (
            <Tooltip title={t('common.delete-all-trades', { ns: 'common' })}>
              <span>
                <IconButton
                  icon="Trashcan"
                  onClick={onDeleteAllTrades}
                  data-testid="trades-delete-button"
                />
              </span>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  ) : null;
};
